import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import Icon from '@/components/Icon/Icon';
import { type PlaceOption, PlaceSelect } from '@/components/Select/PlaceSelect';
import { LabelPlace } from '@/features/place/components/LabelPlace/LabelPlace';
import { apiClient } from '@/lib/api/client';
import { getPath } from '@/lib/api/path';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
import { buildPlaceSelectByIdInQueryParams } from './buildPlaceSelectByIdInQueryParams';
type Props = CFormItemProps & {
  value?: string;
  /** GET 等で既に解決済みの拠点名。指定時は閲覧表示・編集トリガーに優先利用する */
  displayName?: string;
  allowEmpty?: boolean;
  readComponent?: ReactNode;
  onChange: (placeId?: string, place?: PlaceOption) => void;
  inputWidth?: number | string;
  inputClassName?: string;
  showPlaceLink?: boolean;
};

/**
 * Card-based Form / PlaceItem
 */
export const CFormPlaceSelectItem = buildCFormItem<Props>((props: Props) => {
  const shouldResolvePlaceMetadata = Boolean(props.value);
  const {
    data: selectedPlace
  } = useQuery({
    queryKey: [getPath('/v2/components/place-select'), props.value] as const,
    queryFn: async () => {
      if (!props.value) return null;
      const res = await apiClient.GET('/v2/components/place-select', {
        params: {
          query: buildPlaceSelectByIdInQueryParams(props.value)
        }
      });
      return res.data?.items[0] ?? null;
    },
    enabled: shouldResolvePlaceMetadata
  });
  const resolvedName = props.displayName ?? selectedPlace?.name;
  const resolvedAvatarUrl = selectedPlace?.avatarUrl;
  const handlePlaceChange = (place?: PlaceOption) => {
    props.onChange(place?.id, place);
  };
  return <CFormItemBase {...props} readComponent={props.readComponent ?? (props.value && resolvedName ? <div className="flex items-center gap-2">
            {props.showPlaceLink && props.value && <Link to="/places/$id" target="_blank" rel="noopener noreferrer" params={{
      id: props.value
    }}>
                <Icon iconName="SquareArrowOutUpRightIcon" />
              </Link>}
            <LabelPlace name={resolvedName} avatarUrl={resolvedAvatarUrl} />
          </div> : <></>)} writeComponent={<PlaceSelect disabled={props.readonly} allowEmpty={props.allowEmpty} value={props.value} displayValue={props.value ? props.displayName : undefined} onChange={handlePlaceChange} width={typeof props.inputWidth === 'number' ? props.inputWidth : undefined} className={props.inputClassName} />} />;
});